/**
 * Field-service unlock for Android hard kiosk (Lock Task).
 *
 * A pinned screen blocks Home, Recents, the shade and Settings, so maintenance needs a way out. The
 * tenant PIN (`tenant.fieldServicePin`) gates it; when no PIN is configured nothing is gated, so an
 * unconfigured fleet is never locked out. State is module-scoped on purpose: the unlock window and the
 * wrong-PIN cooldown must outlive the debug panel that opened the prompt.
 *
 * The PIN is an operational lock against casual tampering at the screen, not a credential — it arrives
 * in the registration payload in the clear and anyone with adb can bypass all of this. Never log it.
 *
 * On top of the PIN gate, Studio's Android kiosk lock switch (`tenant.androidKioskLockEnabled`,
 * default true) can turn the whole feature off. `syncAndroidKioskLockPolicy` is the single entry point
 * that applies that policy — it is serialized so a burst of tenant/registration updates cannot race
 * an apply/exit pair out of order.
 */
import { computed, readonly, ref } from 'vue'
import {
  applyAndroidKioskLock,
  exitAndroidKioskLock,
  isAndroidKioskLockDesired,
  setAndroidKioskLockDesired
} from '@/helpers/androidKioskLock'
import { setAndroidMaintenanceSuppressUntil } from '@/helpers/androidProcessSelfHeal'
import { androidRemoteKey } from '@/helpers/androidRemoteKey'

/** How long Settings stays reachable after a correct PIN before kiosk re-arms. */
export const KIOSK_UNLOCK_WINDOW_MS = 15 * 60 * 1000
/** Wrong attempts before the prompt goes cold, to make guessing a 4-digit PIN tedious. */
export const KIOSK_UNLOCK_MAX_ATTEMPTS = 5
export const KIOSK_UNLOCK_COOLDOWN_MS = 60 * 1000
/** Columns in the on-screen keypad grid; used to translate remote-control arrow keys into moves. */
const KIOSK_KEYPAD_COLS = 3

/** One cell of the on-screen PIN keypad, in grid order (3 columns). */
export type TKioskKeypadCell
  = | { kind: 'digit', digit: string }
    | { kind: 'clear' }
    | { kind: 'submit' }

export const KIOSK_KEYPAD_CELLS: TKioskKeypadCell[] = [
  { kind: 'digit', digit: '1' },
  { kind: 'digit', digit: '2' },
  { kind: 'digit', digit: '3' },
  { kind: 'digit', digit: '4' },
  { kind: 'digit', digit: '5' },
  { kind: 'digit', digit: '6' },
  { kind: 'digit', digit: '7' },
  { kind: 'digit', digit: '8' },
  { kind: 'digit', digit: '9' },
  { kind: 'clear' },
  { kind: 'digit', digit: '0' },
  { kind: 'submit' }
]

const promptOpen = ref(false)
const pinEntry = ref('')
const wrongPin = ref(false)
const failedAttempts = ref(0)
const cooldownRemainingSec = ref(0)
const unlockedUntilMs = ref(0)
/** Focused cell index for remote/D-pad navigation while the prompt is open. */
const keypadFocusIdx = ref(0)

let expectedPin = ''
let pendingResolve: ((granted: boolean) => void) | null = null
let cooldownTimer: ReturnType<typeof setInterval> | null = null
let relockTimer: ReturnType<typeof setTimeout> | null = null
let relockOnForegroundBound = false
/** True after a correct PIN until settle — cancel must not resolve false over an in-flight unlock. */
let submitInFlight = false
/** Settings drops Lock Task. The Wi-Fi sheet sets this false so kiosk stays armed. */
let dropLockOnGrant = true
/** Optional keypad title. Null keeps the Settings copy in CurrentState. */
const promptTitle = ref<string | null>(null)
/** Serializes `syncAndroidKioskLockPolicy` calls so overlapping tenant updates apply/exit in order. */
let syncPolicyTail: Promise<void> = Promise.resolve()

/** DOM marker so the overlay can sync native focus with `keypadFocusIdx` (Android WebView key routing). */
export const KIOSK_KEYPAD_CELL_ATTR = 'data-kiosk-keypad-idx'

function settle(granted: boolean): void {
  // A correct-PIN unlock is mid native exit — do not resolve false over it (ensureServiceAccess /
  // panel teardown / Escape would otherwise strand Settings after Lock Task already dropped).
  if (submitInFlight && !granted) {
    return
  }
  const resolve = pendingResolve
  pendingResolve = null
  promptOpen.value = false
  pinEntry.value = ''
  wrongPin.value = false
  submitInFlight = false
  unbindKeypadKeydownListener()
  promptTitle.value = null
  resolve?.(granted)
}

function startCooldown(): void {
  cooldownRemainingSec.value = Math.ceil(KIOSK_UNLOCK_COOLDOWN_MS / 1000)
  if (cooldownTimer !== null) {
    return
  }
  cooldownTimer = setInterval(() => {
    cooldownRemainingSec.value -= 1
    if (cooldownRemainingSec.value > 0) {
      return
    }
    cooldownRemainingSec.value = 0
    failedAttempts.value = 0
    if (cooldownTimer !== null) {
      clearInterval(cooldownTimer)
      cooldownTimer = null
    }
  }, 1000)
}

/** Cancels a pending re-lock and drops the unlock window immediately (used when the policy turns off). */
function clearRelock(): void {
  if (relockTimer !== null) {
    clearTimeout(relockTimer)
    relockTimer = null
  }
  unlockedUntilMs.value = 0
}

/** Re-arms kiosk when the maintenance window ends, so a screen left in Settings locks itself again. */
function scheduleRelock(): void {
  unlockedUntilMs.value = Date.now() + KIOSK_UNLOCK_WINDOW_MS
  // Native crash/OOM alarm must not yank the technician out of Settings (#1369).
  void setAndroidMaintenanceSuppressUntil(unlockedUntilMs.value)
  if (relockTimer !== null) {
    clearTimeout(relockTimer)
  }
  relockTimer = setTimeout(() => {
    relockTimer = null
    unlockedUntilMs.value = 0
    if (!isAndroidKioskLockDesired()) {
      // Studio turned the policy off while the window was running: stay unlocked.
      return
    }
    void applyAndroidKioskLock()
  }, KIOSK_UNLOCK_WINDOW_MS)
  bindRelockOnForeground()
}

/**
 * The timeout above cannot be trusted alone: while the technician is in Android settings the player is
 * backgrounded, and Android throttles or suspends WebView timers, so a screen could stay unpinned long
 * past the window. Re-check on every return to the foreground; `applyKioskLock` is idempotent.
 */
function bindRelockOnForeground(): void {
  if (relockOnForegroundBound || typeof document === 'undefined') {
    return
  }
  relockOnForegroundBound = true
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState !== 'visible' || isWithinUnlockWindow()) {
      return
    }
    unlockedUntilMs.value = 0
    if (!isAndroidKioskLockDesired()) {
      return
    }
    void applyAndroidKioskLock()
  })
}

/** True while a technician may reach Android settings without entering the PIN again. */
function isWithinUnlockWindow(): boolean {
  return unlockedUntilMs.value > Date.now()
}

/**
 * Applies (or drops) Studio's Android kiosk lock policy. Serialized behind `syncPolicyTail` so a
 * fast sequence of tenant updates cannot let an older "apply" resolve after a newer "exit" (or vice
 * versa). The desired flag itself is set synchronously so `ensureServiceAccess`/`applyAndroidKioskLock`
 * see the latest policy immediately, even while a previous sync is still in flight.
 */
export function syncAndroidKioskLockPolicy(enabled: boolean): Promise<void> {
  setAndroidKioskLockDesired(enabled)
  const run = syncPolicyTail.then(() => runSyncAndroidKioskLockPolicy(enabled))
  syncPolicyTail = run.catch(() => {})
  return run
}

async function runSyncAndroidKioskLockPolicy(enabled: boolean): Promise<void> {
  if (!enabled) {
    clearRelock()
    // Mid-prompt policy off: ungate the in-flight ensureServiceAccess (same outcome as calling it
    // with desired off). settle(false) would leave Settings unopened until the technician retries.
    settle(pendingResolve != null)
    await exitAndroidKioskLock()
    return
  }
  if (isWithinUnlockWindow()) {
    // A technician is mid-maintenance; do not yank them out because a status refresh landed.
    return
  }
  await applyAndroidKioskLock()
  if (!isAndroidKioskLockDesired()) {
    // Policy flipped off while the apply call was in flight: undo it instead of leaving a stale lock.
    await exitAndroidKioskLock()
  }
}

/** Reads the last persisted registration snapshot and applies the Android kiosk lock policy from it. */
export async function bootstrapAndroidKioskLockAtStartup(): Promise<void> {
  let enabled = true
  try {
    const { default: DBService } = await import('@/services/DBService')
    const snap = await new DBService().getRegistrationStatusSnapshot()
    enabled = snap?.tenant?.androidKioskLockEnabled !== false
  }
  catch {
    // IndexedDB may be unavailable this early in boot; default stays enabled (locked).
  }
  await syncAndroidKioskLockPolicy(enabled)
}

/** True while the field-service PIN prompt is open; lets other input handlers yield to the keypad. */
export function isKioskServicePromptOpen(): boolean {
  return promptOpen.value
}

function normalizeKeypadIdx(idx: number): number {
  const len = KIOSK_KEYPAD_CELLS.length
  return ((idx % len) + len) % len
}

function focusKeypadCell(idx: number): void {
  keypadFocusIdx.value = normalizeKeypadIdx(idx)
}

function activateKeypadCell(idx: number): void {
  const cell = KIOSK_KEYPAD_CELLS[idx]
  if (cell == null) {
    return
  }
  if (cell.kind === 'digit') {
    if (cooldownRemainingSec.value > 0) {
      return
    }
    appendDigitInternal(cell.digit)
    return
  }
  if (cell.kind === 'clear') {
    clearEntryInternal()
    return
  }
  // Match the on-screen OK gate: ignore until 4 digits (short entries would count as wrong attempts).
  if (pinEntry.value.length < 4 || cooldownRemainingSec.value > 0) {
    return
  }
  void submitPinInternal()
}

function resolveKeypadNavKey(evt: KeyboardEvent): string {
  return androidRemoteKey(evt)
}

function keypadKeydownListener(evt: KeyboardEvent): void {
  const key = resolveKeypadNavKey(evt)
  switch (key) {
    case 'ArrowRight':
      evt.preventDefault()
      evt.stopPropagation()
      focusKeypadCell(keypadFocusIdx.value + 1)
      return
    case 'ArrowLeft':
      evt.preventDefault()
      evt.stopPropagation()
      focusKeypadCell(keypadFocusIdx.value - 1)
      return
    case 'ArrowDown':
      evt.preventDefault()
      evt.stopPropagation()
      focusKeypadCell(keypadFocusIdx.value + KIOSK_KEYPAD_COLS)
      return
    case 'ArrowUp':
      evt.preventDefault()
      evt.stopPropagation()
      focusKeypadCell(keypadFocusIdx.value - KIOSK_KEYPAD_COLS)
      return
    case 'Enter':
      evt.preventDefault()
      evt.stopPropagation()
      activateKeypadCell(keypadFocusIdx.value)
      return
    case 'Escape':
      evt.preventDefault()
      evt.stopPropagation()
      if (!submitInFlight) {
        settle(false)
      }
      return
    case 'Backspace':
      evt.preventDefault()
      evt.stopPropagation()
      pinEntry.value = pinEntry.value.slice(0, -1)
      wrongPin.value = false
      return
    default:
      if (/^\d$/.test(key)) {
        evt.preventDefault()
        evt.stopPropagation()
        if (cooldownRemainingSec.value <= 0) {
          appendDigitInternal(key)
        }
      }
  }
}

/**
 * Captures keydown ahead of the toolbar's own listener and the global back/keypress handlers, so the
 * modal PIN prompt owns remote-control input while it is open (see `isKioskServicePromptOpen`).
 */
function bindKeypadKeydownListener(): void {
  if (typeof window === 'undefined') {
    return
  }
  keypadFocusIdx.value = 0
  // Box D-pad/OK is injected on window. A document listener never sees it.
  window.addEventListener('keydown', keypadKeydownListener, true)
}

function unbindKeypadKeydownListener(): void {
  if (typeof window === 'undefined') {
    return
  }
  window.removeEventListener('keydown', keypadKeydownListener, true)
}

function appendDigitInternal(digit: string): void {
  if (pinEntry.value.length >= 6) {
    return
  }
  wrongPin.value = false
  pinEntry.value += digit
}

function clearEntryInternal(): void {
  pinEntry.value = ''
  wrongPin.value = false
}

async function submitPinInternal(): Promise<void> {
  if (cooldownRemainingSec.value > 0 || submitInFlight) {
    return
  }
  if (pinEntry.value !== expectedPin) {
    wrongPin.value = true
    pinEntry.value = ''
    failedAttempts.value += 1
    if (failedAttempts.value >= KIOSK_UNLOCK_MAX_ATTEMPTS) {
      startCooldown()
      settle(false)
    }
    return
  }
  // Hold through the native unlock await. Back/Escape/panel teardown call cancel in that window —
  // ignoring them keeps ensureServiceAccess from resolving false after Lock Task has already dropped.
  submitInFlight = true
  failedAttempts.value = 0
  try {
    if (dropLockOnGrant) {
      await exitAndroidKioskLock()
    }
    scheduleRelock()
    settle(true)
  }
  finally {
    submitInFlight = false
  }
}

/**
 * Keep an open PIN prompt aligned with live registration-status. Studio may rotate or clear
 * `fieldServicePin` while the keypad is up — without this, `submitPin` would keep validating the
 * snapshot captured at prompt open.
 *
 * PIN-clear unlock shares `syncPolicyTail` with `syncAndroidKioskLockPolicy` so a TENANT_UPDATE that
 * both refreshes policy and clears the PIN cannot re-apply Lock Task (or settle false) before the
 * ungated unlock finishes.
 */
export function refreshKioskPromptExpectedPin(configuredPin: string | null | undefined): void {
  if (!promptOpen.value) {
    return
  }
  const pin = (configuredPin ?? '').trim()
  if (pin === '') {
    // Open the maintenance window synchronously so a concurrent syncAndroidKioskLockPolicy(true)
    // queued ahead of our exit cannot re-pin the screen (isWithinUnlockWindow skips apply).
    scheduleRelock()
    const run = syncPolicyTail.then(async () => {
      if (pendingResolve == null) {
        // Already settled (e.g. policy-off sync granted access first).
        return
      }
      if (dropLockOnGrant) {
        await exitAndroidKioskLock()
      }
      scheduleRelock()
      settle(true)
    })
    syncPolicyTail = run.catch(() => {})
    return
  }
  expectedPin = pin
  wrongPin.value = false
}

/** Cancel a pending PIN prompt (remote Back, panel unmount, etc.). */
export function cancelKioskServicePrompt(): void {
  if (submitInFlight) {
    return
  }
  settle(false)
}

export interface TKioskServiceAccessOptions {
  /** Keypad heading. Omit to keep the Settings title. */
  title?: string
  /**
   * When false, granting access does not call exitKioskLock.
   * The site Wi-Fi sheet stays over the armed kiosk. Settings keeps the default (true).
   */
  dropLockTask?: boolean
}

export function useKioskServiceUnlock() {
  const inCooldown = computed(() => cooldownRemainingSec.value > 0)
  const attemptsLeft = computed(() => Math.max(0, KIOSK_UNLOCK_MAX_ATTEMPTS - failedAttempts.value))

  /**
   * Resolves true when the caller may run a privileged action (open Android settings). Unlocks the
   * screen first when needed: no-op when Studio's kiosk lock policy is off or no PIN is configured,
   * otherwise opens the prompt and resolves once the technician gets it right or gives up.
   *
   * Gated on the tenant PIN alone, not on the reported Lock Task state: a screen whose native state is
   * unreadable must still ask, or an unreadable state would become a way past the prompt.
   */
  async function ensureServiceAccess(
    configuredPin: string | null | undefined,
    options?: TKioskServiceAccessOptions
  ): Promise<boolean> {
    const dropLock = options?.dropLockTask !== false
    dropLockOnGrant = dropLock
    if (!isAndroidKioskLockDesired()) {
      // Studio turned kiosk lock off: nothing pinned to unlock, and no PIN prompt needed.
      if (dropLock) {
        await exitAndroidKioskLock()
        clearRelock()
      }
      settle(false)
      return true
    }
    const pin = (configuredPin ?? '').trim()
    if (pin === '' || isWithinUnlockWindow()) {
      // Settings still drops Lock Task. The Wi-Fi sheet does not.
      if (dropLock) {
        await exitAndroidKioskLock()
        scheduleRelock()
      }
      return true
    }
    // Correct PIN already accepted; Lock Task exit still running — join that grant instead of
    // settle(false) which would open a second keypad and steal the pending resolver.
    if (submitInFlight) {
      return new Promise<boolean>((resolve) => {
        const prev = pendingResolve
        pendingResolve = (granted) => {
          prev?.(granted)
          resolve(granted)
        }
      })
    }
    // A prompt left open from an earlier action must not stack a second dialog. Cooldown deliberately
    // still opens the keypad: it is where the countdown is shown, and a dead button teaches a
    // technician nothing.
    settle(false)
    promptTitle.value = options?.title?.trim() || null
    expectedPin = pin
    promptOpen.value = true
    bindKeypadKeydownListener()
    return new Promise<boolean>((resolve) => {
      pendingResolve = resolve
    })
  }

  function cancelPrompt(): void {
    cancelKioskServicePrompt()
  }

  return {
    promptOpen: readonly(promptOpen),
    promptTitle: readonly(promptTitle),
    unlockedUntilMs: readonly(unlockedUntilMs),
    pinEntry: readonly(pinEntry),
    wrongPin: readonly(wrongPin),
    attemptsLeft,
    inCooldown,
    cooldownRemainingSec: readonly(cooldownRemainingSec),
    keypadFocusIdx: readonly(keypadFocusIdx),
    ensureServiceAccess,
    appendDigit: appendDigitInternal,
    clearEntry: clearEntryInternal,
    submitPin: submitPinInternal,
    cancelPrompt,
    focusKeypadCell,
    activateKeypadCell
  }
}

/** Test helper: drop timers and unlock state between cases. */
export function resetKioskServiceUnlockState(): void {
  if (cooldownTimer !== null) {
    clearInterval(cooldownTimer)
    cooldownTimer = null
  }
  if (relockTimer !== null) {
    clearTimeout(relockTimer)
    relockTimer = null
  }
  unbindKeypadKeydownListener()
  promptOpen.value = false
  pinEntry.value = ''
  wrongPin.value = false
  failedAttempts.value = 0
  cooldownRemainingSec.value = 0
  unlockedUntilMs.value = 0
  keypadFocusIdx.value = 0
  expectedPin = ''
  pendingResolve = null
  relockOnForegroundBound = false
  submitInFlight = false
  dropLockOnGrant = true
  promptTitle.value = null
  syncPolicyTail = Promise.resolve()
  setAndroidKioskLockDesired(true)
}
